import LottiePlayer from './LottiePlayer'
import { AnalyticsEventName, FeatureFlags } from '@dupe/common/constants'
import { AnimatePresence, motion } from 'framer-motion'
import Image from 'next/image'
import Link from 'next/link'
import { useSearchParams } from 'next/navigation'
import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'
import AnimatedCounter from '~/src/components/AnimatedCounter'
import useAnalytics from '~/src/hooks/useAnalytics'
import useIsFeatureOn from '~/src/hooks/useIsFeatureOn'
import useGetUserActivityCounts from '~/src/hooks/user/useGetUserActivityCounts'

const RewardsHeaderTrigger = () => {
  const analytics = useAnalytics()
  const router = useRouter()
  const params = useSearchParams()
  const [showUnclaimed, setShowUnclaimed] = useState(false)
  const [hasAnimated, setHasAnimated] = useState(false)
  const [showLottie, setShowLottie] = useState(false)
  const isOwnResultsPage =
    router.route.includes('/[hash]') && !!params?.get('url')

  const { featureOn: rewardsProgramFeatureEnabled } = useIsFeatureOn(
    FeatureFlags.RewardsProgram
  )

  const { data: activityCounts } = useGetUserActivityCounts()

  const totalUnclaimedPoints = activityCounts?.totalUnclaimedPoints ?? 0
  const totalClaimedPoints = activityCounts?.totalClaimedPoints ?? 0

  // Timed transition between claimed and unclaimed
  useEffect(() => {
    let hideTimeout: NodeJS.Timeout | null = null
    if (!hasAnimated && totalUnclaimedPoints > 0) {
      const showTimeout = setTimeout(() => {
        setShowUnclaimed(true)
        hideTimeout = setTimeout(() => {
          setShowUnclaimed(false)
          setHasAnimated(true)
        }, 4000)
      }, 1500)
      return () => {
        clearTimeout(showTimeout)
        if (hideTimeout) clearTimeout(hideTimeout)
      }
    }
  }, [hasAnimated, totalUnclaimedPoints])

  // Show lottie on results page
  useEffect(() => {
    setShowLottie(isOwnResultsPage)
  }, [isOwnResultsPage])

  if (!rewardsProgramFeatureEnabled) return null
  if (!activityCounts) return null
  if (!totalUnclaimedPoints && !totalClaimedPoints) return null

  return (
    <div className='relative'>
      <Link href='/$dupe/rewards'>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className='bg-dupelight flex items-center gap-2 rounded-xl px-2 py-1.5 font-bold text-black md:rounded-2xl md:px-2.5 md:py-2'
          onMouseEnter={() => {
            if (totalUnclaimedPoints > 0) setShowUnclaimed(true)
          }}
          onMouseLeave={() => {
            if (totalUnclaimedPoints > 0 && hasAnimated) setShowUnclaimed(false)
          }}
          onClick={() => {
            analytics.track(AnalyticsEventName.RewardsHeaderTriggerClicked, {
              totalUnclaimedPoints,
              totalClaimedPoints,
              activityCounts,
            })
          }}
        >
          <Image
            src='/assets/activity/single-coin.png'
            alt=''
            width={18}
            height={18}
            className='h-4 w-4 md:h-6 md:w-6'
          />
          <div className='flex items-center gap-1'>
            <AnimatePresence mode='wait' initial={false}>
              {showUnclaimed && totalUnclaimedPoints > 0 ? (
                <motion.div
                  key='unclaimed'
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.2 }}
                  className='flex items-center gap-0.5 text-sm md:text-base'
                >
                  <span>Claim</span>
                  <AnimatedCounter value={totalUnclaimedPoints} height={13} />
                  <span>pts!</span>
                </motion.div>
              ) : (
                <motion.div
                  key='claimed'
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.2 }}
                  className='flex items-center gap-0.5 text-sm md:text-base'
                >
                  <AnimatedCounter
                    value={totalClaimedPoints || totalUnclaimedPoints}
                    height={13}
                  />
                  <span>pts</span>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </motion.div>
      </Link>
      {showLottie && (
        <div className='pointer-events-none absolute inset-x-0 -bottom-2 z-50 flex justify-center'>
          <LottiePlayer
            src='https://lottie.host/e8c2569d-bda8-4a0a-85cd-8a2b59b92cd0/xOlDBv4CPs.lottie'
            autoplay
            // loop
            className='h-48 w-48'
          />
        </div>
      )}
    </div>
  )
}

export default RewardsHeaderTrigger
